import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import PlayerRegisterForm from '../components/PlayerRegisterForm';
import LuckyWheelGame from '../components/LuckyWheelGame';
import './LuckyWheelLandingPage.css';

/**
 * 主頁面 - 管理報名與遊戲兩個狀態
 */
function LuckyWheelLandingPage() {
  const [phase, setPhase] = useState('loading'); // loading | register | game | ended | already_played
  const [config, setConfig] = useState(null);
  const [playerInfo, setPlayerInfo] = useState(null);
  const [error, setError] = useState(null);
  const bgmRef = useRef(null);
  const bgmStartedRef = useRef(false);

  // 嘗試播放背景音樂（需要使用者互動後才能播放）
  const tryPlayBgm = useCallback(() => {
    if (bgmStartedRef.current) return;
    if (bgmRef.current) {
      bgmRef.current.play().then(() => {
        bgmStartedRef.current = true;
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => tryPlayBgm();
    document.addEventListener('click', handler, { once: false });
    document.addEventListener('touchstart', handler, { once: false });
    return () => {
      document.removeEventListener('click', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [tryPlayBgm]);

  const loadConfig = useCallback(async () => {
    try {
      const data = await api.getWheelConfig();
      setConfig(data);
      if (!data.active) {
        setPhase('ended');
        return;
      }

      // 檢查是否已有 token（已報名）
      const existingToken = localStorage.getItem('playerToken');
      if (existingToken) {
        try {
          const meData = await api.getMe();
          setPlayerInfo(meData);
          if (meData.canSpin) {
            setPhase('game');
          } else {
            setPhase('already_played');
          }
        } catch (e) {
          // token 無效，清除並顯示註冊表單
          api.clearPlayerToken();
          setPhase('register');
        }
      } else {
        setPhase('register');
      }
    } catch (err) {
      setError('載入活動資訊失敗，請重新整理頁面');
      setPhase('register');
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleRegisterSuccess = (result, formData) => {
    setPlayerInfo({ ...result, formData });
    if (!result.canSpin) {
      setPhase('already_played');
    } else {
      setPhase('game');
    }
  };

  const handleSpinComplete = () => {
    setPhase('already_played');
  };

  if (phase === 'loading') {
    return (
      <div className="landing-page">
        <div className="app-background" />
        <div className="loading-container fade-in">
          <div className="loading-spinner" />
          <p className="loading-text">載入中...</p>
        </div>
      </div>
    );
  }

  if (phase === 'ended') {
    return (
      <div className="landing-page">
        <div className="app-background" />
        <div className="ended-container slide-up">
          <h1 className="ended-title">活動已結束</h1>
          <p className="ended-desc">感謝您的關注，敬請期待下次活動！</p>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page">
      <div className="app-background" />
      <audio ref={bgmRef} src={`${process.env.PUBLIC_URL}/bgm.mp3`} loop preload="auto" />

      {/* 頂部標題區 */}
      <header className="landing-header slide-up">
        <div className="header-decoration">✦</div>
        <h1 className="landing-title">
          {config?.title || '幸運大轉盤'}
        </h1>
        <p className="landing-subtitle">
          {config?.subtitle || '轉動命運，贏取豐厚大獎'}
        </p>
        <div className="header-line" />
      </header>

      <main className="landing-main">
        {error && (
          <div className="global-error fade-in">{error}</div>
        )}

        {phase === 'register' && (
          <div className="register-section slide-up">
            <PlayerRegisterForm onSuccess={handleRegisterSuccess} termsContent={config?.termsContent} />
          </div>
        )}

        {phase === 'game' && config && (
          <div className="game-section fade-in">
            <LuckyWheelGame
              config={config}
              playerInfo={playerInfo}
              onSpinComplete={handleSpinComplete}
            />
          </div>
        )}

        {phase === 'already_played' && (
          <div className="already-played-section slide-up">
            <div className="glass-card already-played-card">
              <div className="ap-icon">🎊</div>
              <h2 className="ap-title">感謝參與</h2>
              <p className="ap-desc">
                {playerInfo?.message || '您已經參加過本次活動，感謝您的支持！'}
              </p>
              <p className="ap-hint">期待下次活動再與您相見</p>
            </div>
          </div>
        )}
      </main>

      <footer className="landing-footer">
        <p>© 2026 幸運大轉盤活動 · All Rights Reserved</p>
      </footer>
    </div>
  );
}

export default LuckyWheelLandingPage;
